import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { decimalNumberRegex } from '@src/utils/regex';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const ProposedCreditAmountTableCell = ({ getValue, row, column, table, cell }) => {
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);
  const [mitigationType, setMitigationType] = useState();
  const rowMitigationType = useMemo(() => row.getValue('mitigationType'), [row]);
  const debouncedUpdateRef = useRef();

  useEffect(() => {
    debouncedUpdateRef.current = debounce((newValue) => {
      if (tableMeta?.updateData) {
        tableMeta?.updateData(
          row.index,
          column.id,
          columnMeta?.type === 'number' ? Number(newValue) : (newValue ?? newValue)
        );
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  useEffect(() => {
    rowMitigationType && setMitigationType(rowMitigationType);
  }, [rowMitigationType]);

  const handleBlur = (e) => {
    // Clear field if value is 0 or is a negative number
    if (String(e?.target?.value) === '0' || String(e?.target?.value)[0] === '-') {
      setValue(null);
      updateValue(null);
    }
  };

  const handleChange = (e) => {
    const inputValue = e?.target?.value ?? '';
    if (decimalNumberRegex.test(inputValue) || inputValue === '') {
      setValue(inputValue === '' ? null : inputValue);
      updateValue(inputValue === '' ? null : inputValue);
    }
  };

  return columnMeta?.type === 'select' ? (
    <select
      aria-label={'Proposed Credit Amount'}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value ?? ''}
      required={columnMeta?.required || mitigationType === 'Mitigation Bank' || mitigationType === 'In-lieu Fee'}
      disabled={columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee')}
      style={{ width: '100%' }}
    >
      <option key={0} value='' className='none' style={{ display: 'none' }}>
        -- Select a value --
      </option>
      {columnMeta?.options?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  ) : (
    <input
      aria-label={'Proposed Credit Amount'}
      disabled={columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee')}
      id={cell.id}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={
        columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee') ? 'N/A' : ''
      }
      readOnly={columnMeta?.readOnly}
      required={columnMeta?.required}
      style={{
        cursor:
          columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee')
            ? 'not-allowed'
            : 'auto',
        width: '100%',
        borderColor: 'hsl(0, 0%, 80%)',
      }}
      type={columnMeta?.type || 'text'}
      value={value ?? ''}
    />
  );
};
